import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const StockAlertButton = ({ product, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/stock-alerts", {
        product_id: product.id,
        email: email.trim(),
      });
      setDone(true);
      toast.success("We'll email you when it's back");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        data-testid={`stock-alert-open-${product.id}`}
        onClick={() => setOpen(true)}
        variant="outline"
        className={`rounded-full bg-transparent border-border text-foreground hover:bg-secondary ${className}`}
      >
        <Bell className="w-4 h-4 mr-2" />
        Notify me
      </Button>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) {
            setDone(false);
            setEmail("");
          }
        }}
      >
        <DialogContent
          className="max-w-sm rounded-2xl"
          data-testid="stock-alert-dialog"
        >
          <DialogTitle className="font-serif text-2xl">
            {done ? "You're on the list" : `Notify me when ${product.strain_name} is back`}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {done
              ? `We'll email ${email} the moment ${product.dispensary_name} restocks.`
              : "We'll email you the moment this dispensary restocks it. No spam, ever."}
          </DialogDescription>

          {!done && (
            <div className="space-y-3 pt-2">
              <Input
                data-testid="stock-alert-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="h-11"
              />
              <Button
                data-testid="stock-alert-submit"
                onClick={submit}
                disabled={loading || !email.includes("@")}
                className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Bell className="w-4 h-4 mr-2" /> Email me when back
                  </>
                )}
              </Button>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center">
                One email per restock · 21+ only
              </p>
              {/* Soft account nudge — entirely optional. The alert itself
                  works without an account; signing in just centralizes
                  every alert in one place. */}
              <p className="text-[11px] text-foreground/60 text-center leading-relaxed">
                Want all your alerts and saved finds in one place?{" "}
                <a
                  href="/account"
                  className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
                  data-testid="stock-alert-create-account"
                >
                  Create a free account
                </a>
                .
              </p>
            </div>
          )}
          {done && (
            <div className="flex justify-center pt-2">
              <CheckCircle2 className="w-12 h-12 text-emerald-400" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
